// Presentation component: renders filters, table, and pagination (Step 4 confirms consistent zero-based handling).
import React, {useEffect, useState} from 'react';
import {MainLayout} from '../../components/MainLayout';
import './CertificatesPage.scss';
import {format} from "date-fns";

const CertificatesPage = ({
                              certificates,
                              loading,
                              pagination,
                              filters,
                              statusOptions,
                              onPageChange,
                              onPageSizeChange,
                              onFilterChange,
                              onRefresh,
                              onExport,
                              onRevokeCertificate ,
                              tab,
                          }) => {
    const [showFilters, setShowFilters] = useState(false);
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleLocalFilterChange = (field, value) => {
        setLocalFilters({...localFilters, [field]: value});
    };

    const applyFilters = () => {
        onFilterChange(localFilters);
        setShowFilters(false);
    };

    const resetFilters = () => {
        const defaultFilters = {
            serialNumber: '',
            status: '',
            taxNumber: '',
            issuedFromStart: '',
            issuedFromEnd: '',
            expiryFromStart: '',
            expiryFromEnd: '',
            activityNumber: '',
        };
        setLocalFilters(defaultFilters);
        onFilterChange(defaultFilters);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return format(new Date(dateString), "dd-MM-yyyy");
    };

    // Render active filter badges
    const renderFilterBadges = () => {
        const activeBadges = [];

        Object.entries(filters).forEach(([key, value]) => {
            if (value && !['sortField', 'sortDirection'].includes(key)) {
                let displayName = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .trim();

                let displayValue = value;

                // Format status display
                if (key === 'status' && statusOptions.length) {
                    const status = statusOptions.find(opt => opt.value === value);
                    if (status) displayValue = status.label;
                }

                activeBadges.push(
                    <span key={key} className="filter-badge">
                        {`${displayName}: ${displayValue}`}
                        <button
                            onClick={() => {
                                const newFilters = {...filters, [key]: ''};
                                setLocalFilters(newFilters);
                                onFilterChange(newFilters);
                            }}
                        >
                            ×
                        </button>
                    </span>
                );
            }
        });

        if (activeBadges.length === 0) return null;

        return (
            <div className="filter-badges-container">
                <span className="filter-badges-label">Active filters:</span>
                {activeBadges}
                {activeBadges.length > 1 && (
                    <button
                        className="btn btn-link"
                        onClick={resetFilters}
                    >
                        Clear all
                    </button>
                )}
            </div>
        );
    };

    const renderPagination = () => {
        const currentPage = pagination.page;
        const totalPages = pagination.totalPages;

        const canPrev = currentPage > 0;
        const canNext = totalPages > 0 && currentPage < totalPages - 1;

        const pages = [];
        const start = Math.max(0, currentPage - 2);
        const end = Math.min(Math.max(0, totalPages - 1), currentPage + 2);

        for (let i = start; i <= end; i++) {
            pages.push(<button
                key={i}
                className={`pagination-button ${i === currentPage ? 'active' : ''}`}
                onClick={() => onPageChange(i)}
                disabled={loading || i === currentPage}
            >
                {i + 1}
            </button>);
        }

        return (<div className="pagination-container">
            <div className="page-size-selector">
                <label htmlFor="pageSize">Rows per page:</label>
                <select
                    id="pageSize"
                    value={pagination.size}
                    onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
                    disabled={loading}
                >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
            </div>

            <div className="pagination-info">
                {pagination.totalElements > 0
                    ? `${pagination.page * pagination.size + 1}-${Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of ${pagination.totalElements}`
                    : '0 records'}
            </div>

            <div className="pagination-controls">
                <button
                    className="pagination-button"
                    onClick={() => onPageChange(0)}
                    disabled={loading || !canPrev}
                >
                    « First
                </button>
                <button
                    className="pagination-button"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={loading || !canPrev}
                >
                    ‹ Prev
                </button>
                {pages}
                <button
                    className="pagination-button"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={loading || !canNext}
                >
                    Next ›
                </button>
                <button
                    className="pagination-button"
                    onClick={() => onPageChange(Math.max(0, totalPages - 1))}
                    disabled={loading || !canNext}
                >
                    Last »
                </button>
            </div>
        </div>);
    };

    return (<MainLayout tab={tab} withSidebar={true}>
        <div className="certificates-dashboard">
            <div className="card">
                <div className="card-header">
                    <h1 className="card-title">Certificates</h1>
                    <div className="card-actions">
                        <button className="btn btn-outline" onClick={() => setShowFilters(!showFilters)}>
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </button>
                        <button className="btn btn-outline" onClick={onRefresh} disabled={loading}>
                            Refresh
                        </button>
                        <button className="btn btn-outline" onClick={onExport} disabled={loading}>
                            Export
                        </button>
                    </div>
                </div>

                {renderFilterBadges()}

                <div className="card-content">
                    {/* Filters */}
                    {showFilters && (<div className="filters-container">
                        <div className="filters-grid">
                            <div className="filter-item">
                                <label htmlFor="serialNumber">Serial Number</label>
                                <input
                                    id="serialNumber"
                                    type="text"
                                    value={localFilters.serialNumber || ''}
                                    onChange={(e) => handleLocalFilterChange('serialNumber', e.target.value)}
                                />
                            </div>

                            <div className="filter-item">
                                <label htmlFor="taxNumber">Tax Number</label>
                                <input
                                    id="taxNumber"
                                    type="text"
                                    value={localFilters.taxNumber || ''}
                                    onChange={(e) => handleLocalFilterChange('taxNumber', e.target.value)}
                                />
                            </div>
                            <div className="filter-item">
                                <label htmlFor="taxNumber">Activity Number</label>
                                <input
                                    id="activityNumber"
                                    type="text"
                                    value={localFilters.activityNumber || ''}
                                    onChange={(e) => handleLocalFilterChange('activityNumber', e.target.value)}
                                />
                            </div>

                            <div className="filter-item">
                                <label htmlFor="status">Status</label>
                                <select
                                    id="status"
                                    value={localFilters.status || ''}
                                    onChange={(e) => handleLocalFilterChange('status', e.target.value)}
                                >
                                    <option value="">All</option>
                                    {statusOptions.map((opt) => (<option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>))}
                                </select>
                            </div>

                            <div className="filter-item">
                                <label htmlFor="issuedFromStart">Issued From (Start)</label>
                                <input
                                    id="issuedFromStart"
                                    type="date"
                                    value={localFilters.issuedFromStart || ''}
                                    onChange={(e) => handleLocalFilterChange('issuedFromStart', e.target.value)}
                                />
                            </div>

                            <div className="filter-item">
                                <label htmlFor="issuedFromEnd">Issued From (End)</label>
                                <input
                                    id="issuedFromEnd"
                                    type="date"
                                    value={localFilters.issuedFromEnd || ''}
                                    onChange={(e) => handleLocalFilterChange('issuedFromEnd', e.target.value)}
                                />
                            </div>

                            <div className="filter-item">
                                <label htmlFor="expiryFromStart">Expiry From (Start)</label>
                                <input
                                    id="expiryFromStart"
                                    type="date"
                                    value={localFilters.expiryFromStart || ''}
                                    onChange={(e) => handleLocalFilterChange('expiryFromStart', e.target.value)}
                                />
                            </div>

                            <div className="filter-item">
                                <label htmlFor="expiryFromEnd">Expiry From (End)</label>
                                <input
                                    id="expiryFromEnd"
                                    type="date"
                                    value={localFilters.expiryFromEnd || ''}
                                    onChange={(e) => handleLocalFilterChange('expiryFromEnd', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="filters-actions">
                            <button className="btn btn-secondary" onClick={resetFilters}>
                                Reset
                            </button>
                            <button className="btn btn-primary" onClick={applyFilters}>
                                Apply Filters
                            </button>
                        </div>
                    </div>)}

{/* Table */}
                    <div className="table-container">
                        <table className="certificates-table">
                            <thead>
                            <tr>
                                <th>Serial Number</th>
                                <th>Tax Number</th>
                                <th>Activity Number</th>
                                <th>Status</th>
                                <th>Issued Date</th>
                                <th>Expiry Date</th>
                                <th>Revocation Date</th>
                                <th>Revocation Reason</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (<tr>
                                <td colSpan={9} className="table-loading">
                                    <div className="loader"></div>
                                </td>
                            </tr>) : certificates.length === 0 ? (<tr>
                                <td colSpan={9} className="no-data">
                                    No certificates found
                                </td>
                            </tr>) : (certificates.map((c) => (<tr key={c.id}>
                                <td>{c.serialNumber || '-'}</td>
                                <td>{c.taxNumber || '-'}</td>
                                <td>{c.activityNumber || '-'}</td>
                                <td>{c.status || '-'}</td>
                                <td>{formatDate(c.issuedDate)}</td>
                                <td>{formatDate(c.expiryDate)}</td>
                                <td>{formatDate(c.revocationDate)}</td>
                                <td>{c.revocationReason || '-'}</td>
                                <td>
                                    <button 
                                        className="btn btn-outline"
                                        onClick={() => onRevokeCertificate(c.serialNumber)}
                                        disabled={c.status === 'REVOKED' || c.status === 'CANCELLED'}
                                    >
                                        Revoke
                                    </button>
                                </td>
                            </tr>)))}
                            </tbody>
                        </table>
                    </div>
                    {renderPagination()}
                </div>
            </div>
        </div>
    </MainLayout>);
};

export default CertificatesPage;
